import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useEndpointStore } from '@/stores/endpointStore';
import { useCloudServiceStore } from '@/stores/cloudServiceStore';
import { useCloudResourceStore } from '@/stores/cloudResourceStore';
import { useInternetConnectionStore } from '@/stores/internetConnectionStore';
import { useDMZConfigStore } from '@/stores/dmzConfigStore';
import { useSupplierStore } from '@/stores/supplierStore';
import { useRisks, useTasks, useEvidence, useControls, useIncidents } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useCallback } from 'react';
import type { EntityType } from '@/types/link';

export function useEntityNameResolver() {
  const { organization } = useAuth();
  const { sites } = useSiteStore();
  const { zones } = useZoneStore();
  const { systems } = useSystemStore();
  const { endpoints } = useEndpointStore();
  const { cloudServices } = useCloudServiceStore();
  const { cloudResources } = useCloudResourceStore();
  const { connections: internetConnections } = useInternetConnectionStore();
  const { configs: dmzConfigs } = useDMZConfigStore();
  const { suppliers } = useSupplierStore();
  const { risks } = useRisks(organization?.id);
  const { tasks } = useTasks(organization?.id);
  const { evidence } = useEvidence(organization?.id);
  const { controls } = useControls(organization?.id);
  const { incidents } = useIncidents(organization?.id);

  const resolveEntityName = useCallback((type: EntityType, id: string): string => {
    switch (type) {
      case 'site': {
        const site = sites.find(s => s.id === id);
        return site?.name || id;
      }
      case 'zone': {
        const zone = zones.find(z => z.id === id);
        return zone?.name || id;
      }
      case 'system': {
        const system = systems.find(s => s.id === id);
        return system?.name || id;
      }
      case 'endpoint': {
        const endpoint = endpoints.find(e => e.id === id);
        return endpoint?.name || id;
      }
      case 'cloud':
      case 'cloud_resource': {
        if (type === 'cloud') {
          const cs = cloudServices.find(c => c.id === id);
          return cs?.name || id;
        }
        const cr = cloudResources.find(c => c.id === id);
        return cr?.name || id;
      }
      case 'internet_connection': {
        const conn = internetConnections.find(c => c.id === id);
        return conn?.label || id;
      }
      case 'dmz_config': {
        const dmz = dmzConfigs.find(d => d.id === id);
        return dmz?.label || id;
      }
      case 'supplier': {
        const supplier = suppliers.find(s => s.id === id);
        return supplier?.name || id;
      }
      case 'risk': {
        const risk = risks?.find(r => r.id === id);
        return risk?.title || id;
      }
      case 'task': {
        const task = tasks?.find(t => t.id === id);
        return task?.title || id;
      }
      case 'control': {
        const control = controls?.find(c => c.id === id);
        return control?.title || id;
      }
      case 'incident': {
        const incident = incidents?.find(i => i.id === id);
        return incident?.title || id;
      }
      case 'evidence': {
        const ev = evidence?.find(e => e.id === id);
        return ev?.name || id;
      }
      default:
        return id;
    }
  }, [sites, zones, systems, endpoints, cloudServices, cloudResources, internetConnections, dmzConfigs, suppliers, risks, tasks, controls, incidents, evidence]);

  return { resolveEntityName };
}
